/**
 * Configuration for experience chapter pages (/upplevelse/:slug).
 * Single source of truth for all experience directions.
 *
 * This mapping is canonical — it drives:
 *   - Landing direction cards (ExperienceSection)
 *   - ExperienceHeader on chapter pages
 *   - Section titles on chapter pages
 *
 * Explore = dynamisk översikt (proximity ok)
 * Upplevelse = kuraterad landning (proximity aldrig)
 */

export interface ExperienceConfig {
  slug: string;
  /** Label shown in ExperienceSection cards */
  label: string;
  /** H1 on the chapter page */
  title: string;
  /** One-line subtitle shown below H1 */
  subtitle: string;
  /** Introductory paragraph (max 3 lines, optional) */
  intro: string;
  /** Curated places section title, e.g. "Platser i rörelse" */
  placesSectionTitle: string;
  /** Curated activities section title, e.g. "Aktiviteter i rörelse" */
  activitiesSectionTitle: string;
  /** Tags used to weight/curate places */
  placeTags: string[];
  /** Tags used to weight/curate events */
  eventTags: string[];
  /** Max spots filter for events (optional) */
  maxSpotsFilter?: number;
}

export const EXPERIENCE_CONFIGS: ExperienceConfig[] = [
  {
    slug: 'vara-tillsammans',
    label: 'Vara tillsammans',
    title: 'Vara tillsammans',
    subtitle: 'Platser för stunder som stannar kvar.',
    intro: 'Platser där ni kan vara nära, utan att det behöver vara stort. Små möten, öppna vyer och rum för samtal.',
    placesSectionTitle: 'Platser att dela',
    activitiesSectionTitle: 'Aktiviteter att göra ihop',
    placeTags: ['lugn', 'utsikt', 'fika', 'liten skala', 'romantisk'],
    eventTags: ['tasting', 'workshop', 'dinner'],
    maxSpotsFilter: 20,
  },
  {
    slug: 'gora-nagot-med-barnen',
    label: 'Göra något med barnen',
    title: 'Göra något med barnen',
    subtitle: 'Upplevelser som passar hela familjen.',
    intro: 'Djur, natur och saker att göra med händerna.',
    placesSectionTitle: 'Platser för barn och familj',
    activitiesSectionTitle: 'Aktiviteter för familjen',
    placeTags: ['barn', 'familj', 'djur', 'natur', 'pedagogisk'],
    eventTags: ['family', 'workshop', 'outdoor'],
  },
  {
    slug: 'lara-dig-nagot-nytt',
    label: 'Lära dig något nytt',
    title: 'Lära dig något nytt',
    subtitle: 'Kurser, workshops och hantverk.',
    intro: 'Lär dig något du inte visste att du ville kunna.',
    placesSectionTitle: 'Platser där du lär dig',
    activitiesSectionTitle: 'Kurser och workshops',
    placeTags: ['hantverk', 'kurs', 'workshop', 'pedagogisk'],
    eventTags: ['course', 'workshop'],
  },
  {
    slug: 'smaka-och-njuta',
    label: 'Smaka och njuta',
    title: 'Smaka och njuta',
    subtitle: 'Gårdsbutiker, provningar och lokala smaker.',
    intro: 'Måltider lagade med det som växer i närheten.',
    placesSectionTitle: 'Platser att smaka',
    activitiesSectionTitle: 'Provningar och måltider',
    placeTags: ['mat', 'fika', 'gårdsbutik', 'provning', 'restaurang'],
    eventTags: ['tasting', 'dinner', 'food'],
  },
  {
    slug: 'vara-nara-naturen',
    label: 'Vara nära naturen',
    title: 'Vara nära naturen',
    subtitle: 'Stigar, vatten och öppna landskap.',
    intro: 'Platser där naturen får vara huvudpersonen.',
    placesSectionTitle: 'Platser nära naturen',
    activitiesSectionTitle: 'Aktiviteter i naturen',
    placeTags: ['natur', 'skog', 'vatten', 'vandring', 'sjö'],
    eventTags: ['outdoor', 'nature', 'hiking'],
  },
  {
    slug: 'vara-i-rorelse',
    label: 'Vara i rörelse',
    title: 'Vara i rörelse',
    subtitle: 'Leder och aktiva upplevelser.',
    intro: 'För dig som vill känna landskapet i kroppen.',
    placesSectionTitle: 'Platser i rörelse',
    activitiesSectionTitle: 'Aktiviteter i rörelse',
    placeTags: ['vandring', 'cykel', 'paddling', 'löpning', 'aktiv'],
    eventTags: ['outdoor', 'hiking', 'sport'],
  },
];

/** Lookup slug → full URL path mapping for ExperienceSection */
export const EXPERIENCE_SLUG_MAP: Record<string, string> = {
  together: 'vara-tillsammans',
  children: 'gora-nagot-med-barnen',
  learn: 'lara-dig-nagot-nytt',
  taste: 'smaka-och-njuta',
  nature: 'vara-nara-naturen',
  movement: 'vara-i-rorelse',
};

export function getExperienceConfig(slug: string): ExperienceConfig | undefined {
  return EXPERIENCE_CONFIGS.find((c) => c.slug === slug);
}
